import { apiEmployees } from "@/api/employees/employee.api";
import { ApiAuthorization } from "@/types/employees/employee.types.ts";
import { ApiLoginOrganization } from "@/types/security.types.ts";
import { Box, Button, Select, Stack, Title, useToasts } from "@skailer-com/uikit";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface OrganizationSelectFormProps {
  pendingToken: string;
  onSuccess: (authorization: ApiAuthorization) => void;
  onExpired: () => void;
}

export function OrganizationSelectForm({
  pendingToken,
  onSuccess,
  onExpired,
}: OrganizationSelectFormProps) {
  const { t } = useTranslation();
  const { showError } = useToasts();

  const [organizations, setOrganizations] = useState<ApiLoginOrganization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const notifyExpired = useCallback(() => {
    showError({ title: t("core:login_organization_session_expired") });
    onExpired();
  }, [showError, t, onExpired]);

  useEffect(() => {
    let active = true;

    const loadOrganizations = async () => {
      const { data } = await apiEmployees.getLoginOrganizations(pendingToken);
      if (!active) return;

      if (data) {
        setOrganizations(data);
        setIsLoading(false);
      } else {
        notifyExpired();
      }
    };

    void loadOrganizations();

    return () => {
      active = false;
    };
  }, [pendingToken, notifyExpired]);

  const handleSubmit = async () => {
    if (selectedId === null) return;

    setIsSubmitting(true);
    const { data, error } = await apiEmployees.selectLoginOrganization(pendingToken, selectedId);

    if (data) {
      onSuccess(data);
      return;
    }

    setIsSubmitting(false);
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      notifyExpired();
    }
  };

  return (
    <Box className="w-full p-6 lg:p-8 bg-light rounded-xs shadow-md">
      <Stack gap={"2xl"}>
        <Stack gap={"lg"}>
          <Title
            header={t("core:login_select_organization")}
            size={"lg"}
            caption={t("core:login_select_organization_subtitle")}
          />

          <Stack gap="lg">
            <Select
              emptyTitle={t("core:login_organization_placeholder")}
              options={organizations.map((organization) => ({
                id: organization.id,
                title: organization.title,
              }))}
              value={selectedId}
              onChange={(value) => setSelectedId(value as number)}
              isLoading={isLoading}
              searchable={organizations.length > 7}
            />

            <Button
              variant={"secondary"}
              size="lg"
              className="w-full"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              disabled={selectedId === null || isSubmitting}
            >
              {t("common:continue")}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
